import { useState, useEffect, useCallback, useRef } from 'react';
import { Bell, CheckCheck, X, Reply, Send } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow, parseISO } from 'date-fns';
import {
  getNotifications,
  getUnreadCount,
  markNotificationRead,
  markAllNotificationsRead,
  deleteNotification,
  replyToNotification,
} from '@/lib/api';

const REPLY_CHIPS = ['On it 👍', "Can't make it 🙁", 'Question ❓'];

/**
 * Notifications bell. Polls the server every 60s for unread count and
 * exposes a dropdown with recent notifications. Positioned as an inline
 * button so it can be dropped anywhere in the header/content.
 */
export const NotificationsBell = () => {
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [replyingId, setReplyingId] = useState(null);
  const [replyDraft, setReplyDraft] = useState('');
  const [sending, setSending] = useState(false);
  const wrapRef = useRef(null);
  const navigate = useNavigate();

  const refreshCount = useCallback(async () => {
    try {
      const { data } = await getUnreadCount();
      setCount(data.count || 0);
    } catch (err) {
      console.warn('Failed to fetch unread notification count', err);
    }
  }, []);

  const refreshItems = useCallback(async () => {
    try {
      const { data } = await getNotifications();
      setItems(data || []);
    } catch (err) {
      console.warn('Failed to fetch notifications', err);
    }
  }, []);

  useEffect(() => {
    refreshCount();
    const t = setInterval(refreshCount, 60000);
    return () => clearInterval(t);
  }, [refreshCount]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const handleOpen = async () => {
    const nxt = !open;
    setOpen(nxt);
    if (nxt) {
      await refreshItems();
    }
  };

  const handleClick = async (n) => {
    if (replyingId === n.id) return; // clicking on the reply zone shouldn't nav
    if (!n.read) {
      try {
        await markNotificationRead(n.id);
        setCount((c) => Math.max(0, c - 1));
        setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      } catch (err) {
        console.warn('Failed to mark notification read', err);
      }
    }
    if (n.link_url) {
      setOpen(false);
      navigate(n.link_url);
    }
  };

  const sendReply = async (note, text) => {
    if (!text || !text.trim() || sending) return;
    setSending(true);
    try {
      await replyToNotification(note.id, text.trim());
      setItems((prev) =>
        prev.map((x) =>
          x.id === note.id
            ? { ...x, reply_text: text.trim(), replied_at: new Date().toISOString(), read: true }
            : x
        )
      );
      if (!note.read) setCount((c) => Math.max(0, c - 1));
      setReplyingId(null);
      setReplyDraft('');
    } catch (err) {
      console.warn('Failed to reply', err);
    } finally {
      setSending(false);
    }
  };

  const handleMarkAll = async () => {
    try {
      await markAllNotificationsRead();
      setItems((prev) => prev.map((x) => ({ ...x, read: true })));
      setCount(0);
    } catch (err) {
      console.warn('Failed to mark all read', err);
    }
  };

  const handleDelete = async (e, n) => {
    e.stopPropagation();
    try {
      await deleteNotification(n.id);
      setItems((prev) => prev.filter((x) => x.id !== n.id));
      if (!n.read) setCount((c) => Math.max(0, c - 1));
    } catch (err) {
      console.warn('Failed to delete notification', err);
    }
  };

  return (
    <div ref={wrapRef} className="relative inline-block" data-testid="notifications-bell">
      <button
        type="button"
        onClick={handleOpen}
        className="relative p-2 rounded-full hover:bg-slate-100 transition-colors"
        aria-label="Notifications"
        data-testid="notifications-bell-btn"
      >
        <Bell className="h-5 w-5 text-slate-700" />
        {count > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center"
            data-testid="notifications-unread-badge"
          >
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-80 sm:w-96 max-h-[70vh] overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-xl z-50"
          data-testid="notifications-dropdown"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <h3 className="font-semibold text-slate-900 font-manrope">Notifications</h3>
            {items.some((n) => !n.read) && (
              <button
                type="button"
                onClick={handleMarkAll}
                className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
                data-testid="mark-all-read-btn"
              >
                <CheckCheck className="h-3 w-3" />
                Mark all read
              </button>
            )}
          </div>

          {items.length === 0 ? (
            <div className="text-sm text-slate-400 text-center py-10">
              You&apos;re all caught up.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {items.map((n) => {
                const canReply = n.type === 'task_shared' && !n.replied_at;
                const replied = !!n.replied_at;
                const isReplying = replyingId === n.id;
                return (
                <li
                  key={n.id}
                  onClick={() => handleClick(n)}
                  className={`group flex flex-col gap-2 px-4 py-3 cursor-pointer hover:bg-slate-50 transition-colors ${!n.read ? 'bg-indigo-50/40' : ''}`}
                  data-testid={`notification-item-${n.id}`}
                >
                  <div className="flex items-start gap-3">
                    {!n.read && (
                      <span className="mt-1.5 h-2 w-2 rounded-full bg-indigo-500 shrink-0" />
                    )}
                    {n.read && <span className="mt-1.5 h-2 w-2 shrink-0" />}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-900 truncate">
                        {n.title}
                      </p>
                      <p className="text-xs text-slate-600 mt-0.5 line-clamp-2">
                        {n.message}
                      </p>
                      <p className="text-[10px] text-slate-400 mt-1">
                        {n.created_at
                          ? formatDistanceToNow(
                              typeof n.created_at === 'string'
                                ? parseISO(n.created_at)
                                : new Date(n.created_at),
                              { addSuffix: true }
                            )
                          : ''}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => handleDelete(e, n)}
                      className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-slate-200 transition-opacity"
                      aria-label="Dismiss"
                      data-testid={`notification-dismiss-${n.id}`}
                    >
                      <X className="h-3.5 w-3.5 text-slate-400" />
                    </button>
                  </div>

                  {/* Replied state */}
                  {replied && (
                    <div
                      className="ml-5 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 self-start"
                      data-testid={`notification-replied-${n.id}`}
                    >
                      You replied: <span className="font-medium">{n.reply_text}</span>
                    </div>
                  )}

                  {/* Reply CTA */}
                  {canReply && !isReplying && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setReplyingId(n.id);
                        setReplyDraft('');
                      }}
                      className="ml-5 self-start inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-900"
                      data-testid={`notification-reply-open-${n.id}`}
                    >
                      <Reply className="h-3 w-3" /> Quick reply
                    </button>
                  )}

                  {/* Reply composer */}
                  {canReply && isReplying && (
                    <div
                      className="ml-5 rounded border border-slate-200 bg-white p-2 space-y-2"
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`notification-reply-composer-${n.id}`}
                    >
                      <div className="flex flex-wrap gap-1.5">
                        {REPLY_CHIPS.map((chip) => (
                          <button
                            key={chip}
                            type="button"
                            onClick={() => sendReply(n, chip)}
                            className="text-[11px] px-2 py-1 rounded-full border border-slate-200 hover:border-slate-900 hover:bg-slate-900 hover:text-white transition-colors"
                            data-testid={`notification-reply-chip-${n.id}-${chip.slice(0, 4)}`}
                          >
                            {chip}
                          </button>
                        ))}
                      </div>
                      <textarea
                        value={replyDraft}
                        onChange={(e) => setReplyDraft(e.target.value)}
                        placeholder="Or type your own…"
                        rows={2}
                        className="w-full text-xs rounded border border-slate-200 p-2 focus:outline-none focus:ring-1 focus:ring-slate-400"
                        data-testid={`notification-reply-input-${n.id}`}
                      />
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          type="button"
                          onClick={() => {
                            setReplyingId(null);
                            setReplyDraft('');
                          }}
                          className="text-[11px] px-2 py-1 text-slate-500 hover:text-slate-800"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          disabled={!replyDraft.trim() || sending}
                          onClick={() => sendReply(n, replyDraft)}
                          className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded bg-slate-900 text-white disabled:bg-slate-300"
                          data-testid={`notification-reply-send-${n.id}`}
                        >
                          <Send className="h-3 w-3" />
                          Send
                        </button>
                      </div>
                    </div>
                  )}
                </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationsBell;
